interface NetworkEntry {
	method: string
	url: string
	status: number | null
	duration: number
	timestamp: number
	requestBody?: string
	responseBody?: string
	responseHeaders?: Record<string, string>
}

const MAX_ENTRIES = 100
const buffer: NetworkEntry[] = []

const SKIP_PATTERNS = /\.(js|css|woff2?|ttf|eot|png|jpe?g|gif|svg|ico|webp|avif)(\?|$)/i

function addEntry(entry: NetworkEntry) {
	buffer.push(entry)
	if (buffer.length > MAX_ENTRIES) {
		buffer.shift()
	}
}

function safeStringify(value: unknown): string | undefined {
	if (value == null) return undefined
	try {
		const str = typeof value === 'string' ? value : JSON.stringify(value)
		// Cap individual body at 10 KB
		return str.length > 10_000 ? str.slice(0, 10_000) + '…[truncated]' : str
	} catch {
		return undefined
	}
}

function extractHeaders(headers: Headers): Record<string, string> {
	const result: Record<string, string> = {}
	headers.forEach((value, key) => {
		// Skip sensitive headers
		if (/^(authorization|cookie|set-cookie)$/i.test(key)) return
		result[key] = value
	})
	return result
}

export function initNetworkCapture() {
	const originalFetch = window.fetch

	window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
		const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
		if (SKIP_PATTERNS.test(url)) {
			return originalFetch(input, init)
		}

		const method = init?.method?.toUpperCase() || 'GET'
		const start = Date.now()

		try {
			const response = await originalFetch(input, init)
			const entry: NetworkEntry = {
				method,
				url,
				status: response.status,
				duration: Date.now() - start,
				timestamp: start,
				responseHeaders: extractHeaders(response.headers),
			}

			// Only capture bodies for failed requests
			if (response.status >= 400) {
				entry.requestBody = safeStringify(init?.body)
				try {
					const cloned = response.clone()
					const text = await cloned.text()
					entry.responseBody = safeStringify(text)
				} catch {
					// Response body not readable
				}
			}

			addEntry(entry)
			return response
		} catch (error) {
			addEntry({
				method,
				url,
				status: null,
				duration: Date.now() - start,
				timestamp: start,
				requestBody: safeStringify(init?.body),
			})
			throw error
		}
	}

	// Patch XMLHttpRequest
	const originalOpen = XMLHttpRequest.prototype.open
	const originalSend = XMLHttpRequest.prototype.send

	XMLHttpRequest.prototype.open = function (method: string, url: string | URL, ...rest: unknown[]) {
		const urlStr = typeof url === 'string' ? url : url.href
		if (!SKIP_PATTERNS.test(urlStr)) {
			;(this as any)._feedbackMeta = { method: method.toUpperCase(), url: urlStr, start: 0 }
		}
		return (originalOpen as any).apply(this, [method, url, ...rest])
	}

	XMLHttpRequest.prototype.send = function (body?: Document | XMLHttpRequestBodyInit | null) {
		const meta = (this as any)._feedbackMeta
		if (meta) {
			meta.start = Date.now()
			this.addEventListener('loadend', () => {
				const entry: NetworkEntry = {
					method: meta.method,
					url: meta.url,
					status: this.status || null,
					duration: Date.now() - meta.start,
					timestamp: meta.start,
				}
				// Only capture bodies for failed requests
				if (this.status >= 400) {
					entry.requestBody = safeStringify(body)
					try {
						entry.responseBody = safeStringify(this.responseText)
					} catch {
						// Response not readable
					}
				}
				addEntry(entry)
			})
		}
		return originalSend.call(this, body)
	}
}

export function getNetworkLogs(): NetworkEntry[] {
	return [...buffer]
}
